/**
 * Create a group.
 *
 * Only the name is required. Everything else on this form is identity, not
 * configuration — a group with an emoji and a colour is findable in the
 * switcher at a glance, but making any of it mandatory would put four
 * decisions between someone and their first upload.
 */

import { useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ACCENT_COLORS, emojiSchema, groupNameSchema } from "@fc/core";
import type { AccentColor } from "@fc/core";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { createGroupKey } from "../../lib/keyring.js";
import { activeRecoveryKey } from "./escrow.js";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { Field } from "../../components/ui/Field.js";
import { IdentityField } from "../../components/ui/IdentityField.js";
import { Textarea } from "../../components/ui/Input.js";
// The accent swatches are styled alongside the settings screen's own accent
// picker: one definition of the eight colours, used by both places that offer
// them.
import "./GroupSettingsPage.css";

export interface CreateGroupDialogProps {
  onClose: () => void;
  onCreated: (groupId: string) => void;
}

export function CreateGroupDialog({
  onClose,
  onCreated,
}: CreateGroupDialogProps) {
  const formId = useId();
  const queryClient = useQueryClient();
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [emoji, setEmoji] = useState("");
  const [accentColor, setAccentColor] = useState<AccentColor>("violet");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      emoji?: string;
      accentColor: AccentColor;
    }) =>
      /*
       * The group's key is made here, in the browser, before the group exists.
       *
       * It has to be: the server stores the creator's sealed copy in the same
       * transaction that writes the group, so a group can never exist holding
       * photographs nobody has a key for. Creating it server-side would mean
       * the server had held the key, which is the one thing the whole scheme
       * is arranged to avoid.
       */
      activeRecoveryKey().then(async (recovery) => {
        const keys = await createGroupKey(recovery?.publicKey);
        return trpcClient.groups.create.mutate({
          ...input,
          wrappedGroupKey: keys.wrappedGroupKey,
          /* Both or neither — the id is what files the copy against the key
             that opens it. */
          ...(keys.escrowedGroupKey && recovery
            ? {
                escrowedGroupKey: keys.escrowedGroupKey,
                recoveryKeyId: recovery.id,
              }
            : {}),
        });
      }),
    onSuccess: async (result) => {
      // The switcher and the home grid both read `groups.list`; refreshing it
      // before handing control back means the new group is already there when
      // the caller navigates into it.
      await queryClient.invalidateQueries({
        queryKey: trpc.groups.list.queryKey(),
      });
      onCreated(result.groupId);
    },
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();

    // Validated with the same schemas the server uses, so the two can never
    // disagree about what a valid name or emoji is.
    const problems: Record<string, string> = {};

    const nameResult = groupNameSchema.safeParse(name);
    if (!nameResult.success)
      problems.name = nameResult.error.issues[0]!.message;

    const trimmedEmoji = emoji.trim();
    if (trimmedEmoji.length > 0) {
      const emojiResult = emojiSchema.safeParse(trimmedEmoji);
      if (!emojiResult.success)
        problems.emoji = emojiResult.error.issues[0]!.message;
    }

    setFieldErrors(problems);
    if (Object.keys(problems).length > 0) return;

    const trimmedDescription = description.trim();
    create.mutate({
      name: name.trim(),
      accentColor,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
      ...(trimmedEmoji ? { emoji: trimmedEmoji } : {}),
    });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Create a group"
      description="A private space for one set of friends. You can invite people once it exists."
      initialFocus={nameRef}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={create.isPending}
          >
            Create group
          </Button>
        </>
      }
    >
      <form id={formId} className="fc-group-form" onSubmit={submit}>
        {create.isError && (
          <p className="fc-group-form__error" role="alert">
            {errorMessage(create.error)}
          </p>
        )}

        {/*
          Name, glyph and colour as one control, the same one the album dialog
          uses. The emoji was a free-text box you were expected to paste into —
          which is not a choice so much as a dare — and the colour was a row of
          swatches at the far end of the form, three fields away from anything
          it described.
        */}
        <IdentityField
          name={name}
          onNameChange={setName}
          nameError={fieldErrors.name}
          namePlaceholder="Barcelona 2026"
          nameMaxLength={60}
          nameRef={nameRef}
          emoji={emoji}
          onEmojiChange={setEmoji}
          colour={accentColor}
          onColourChange={(next) => {
            if (next !== null) setAccentColor(next);
          }}
        />

        <Field
          label="Description"
          hint="Optional. A line to remind everyone what this group is for."
        >
          <Textarea
            rows={2}
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/* ─────────────────────────────── Accent picker ────────────────────────────── */

export interface AccentPickerProps {
  value: AccentColor;
  onChange: (value: AccentColor) => void;
  disabled?: boolean;
}

const ACCENT_LABELS: Record<AccentColor, string> = {
  violet: "Violet",
  blue: "Blue",
  cyan: "Cyan",
  teal: "Teal",
  lime: "Lime",
  amber: "Amber",
  rose: "Rose",
  slate: "Slate",
};

/**
 * The eight curated group accents. Shared by the create dialog and the group's
 * appearance settings, which must offer exactly the same set.
 *
 * Follows the ARIA radio-group pattern: one tab stop for the whole row, arrows
 * move and select. Tabbing through eight colours to reach the next field would
 * be tedious for anyone using a keyboard full time.
 */
export function AccentPicker({
  value,
  onChange,
  disabled = false,
}: AccentPickerProps) {
  const labelId = useId();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const activeIndex = Math.max(
    0,
    ACCENT_COLORS.findIndex((color) => color === value),
  );

  const select = (index: number) => {
    const next =
      ACCENT_COLORS[(index + ACCENT_COLORS.length) % ACCENT_COLORS.length];
    if (next === undefined) return;
    buttons.current[ACCENT_COLORS.indexOf(next)]?.focus();
    if (next !== value) onChange(next);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        select(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        select(index - 1);
        break;
      case "Home":
        event.preventDefault();
        select(0);
        break;
      case "End":
        event.preventDefault();
        select(ACCENT_COLORS.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="fc-accents">
      <span className="fc-accents__label" id={labelId}>
        Colour
      </span>
      <div
        className="fc-accents__row"
        role="radiogroup"
        aria-labelledby={labelId}
      >
        {ACCENT_COLORS.map((color, index) => (
          <button
            key={color}
            ref={(node) => {
              buttons.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={color === value}
            aria-label={ACCENT_LABELS[color]}
            tabIndex={index === activeIndex ? 0 : -1}
            disabled={disabled}
            className="fc-accents__swatch"
            data-color={color}
            onClick={() => select(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className="fc-accents__dot" aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
